import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import DropdownMenuButton from 'common/components/drop-downs/dropdown-menu-button';
import { DataScienceTask } from '../tasks.models';
import { DataScienceTaskStatus } from '../tasks-status';
import {
  useTriggerTaskRunMutation,
  useUpdateDataScienceTaskMutation
} from 'app/apis/commandApi';
import {
  createTriggerCacheKey,
  createUpdateCacheKey
} from '../task-cache-keys';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { onSuccess } from 'features/toast/toast.slice';
import { getDisabledThemeClasses } from 'common/components/buttons/button-functions';
import { useState } from 'react';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import Tooltip from 'common/components/tooltip/tooltip-v2';
import { useNavigate } from 'react-router-dom';

interface DataScienceTaskTableActionsProps {
  dataScienceTask: DataScienceTask;
}

const DataScienceTaskTableActions = ({
  dataScienceTask
}: DataScienceTaskTableActionsProps) => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [triggerDataScienceEndpointMutation] = useTriggerTaskRunMutation({
    fixedCacheKey: createTriggerCacheKey(
      dataScienceTask.machineLearningModel,
      siteId
    )
  });

  const onEditDataScienceTask = () => {
    navigate(`${dataScienceTask.machineLearningModel}/edit`);
  };

  const onReviewDataScienceTask = () => {
    navigate(`${dataScienceTask.machineLearningModel}/review`);
  };

  const onTriggerDataScienceTask = () => {
    void triggerDataScienceEndpointMutation({
      siteId,
      machineLearningModel: dataScienceTask.machineLearningModel
    })
      .unwrap()
      .then(() =>
        dispatch(
          onSuccess({
            title: 'Data Science Task Triggered',
            description: `The Webhook '${dataScienceTask.machineLearningModel}' has been triggered successfully.`
          })
        )
      );
  };
  const isProcessing =
    dataScienceTask.status === DataScienceTaskStatus.PROCESSING;

  const [updateDataScienceTaskMutation] = useUpdateDataScienceTaskMutation({
    fixedCacheKey: createUpdateCacheKey(
      dataScienceTask.machineLearningModel,
      siteId
    )
  });

  const onDisableDataScienceTask = () => {
    const disabledDataScienceTask = {
      siteId,
      dataScienceTask: {
        ...dataScienceTask,
        status: DataScienceTaskStatus.DISABLED
      }
    };

    void updateDataScienceTaskMutation(disabledDataScienceTask).then(() =>
      dispatch(
        onSuccess({
          title: 'Data Science Task Disabled',
          description: `The Data Science Task '${dataScienceTask.machineLearningModel}' has been disabled successfully.`
        })
      )
    );
  };

  const onEnableDataScienceTask = () => {
    const enabledDataScienceTask = {
      siteId,
      dataScienceTask: {
        ...dataScienceTask,
        status: DataScienceTaskStatus.SCHEDULED
      }
    };

    void updateDataScienceTaskMutation(enabledDataScienceTask).then(() =>
      dispatch(
        onSuccess({
          title: 'Data Science Task Enabled',
          description: `The Data Science Task '${dataScienceTask.machineLearningModel}' has been enabled successfully.`
        })
      )
    );
  };

  const renderTriggerButton = () => {
    return (
      <Tooltip
        key={'trigger-button-tooltip'}
        isDisabled={isProcessing}
        tooltipContent={<div className="whitespace-nowrap">Re-Trigger</div>}
      >
        <i
          className={`${
            isProcessing ? '' : 'group-hover:text-success-400'
          } cursor-pointer ra-icons-generic-play mr-1 text-neutral-500 rounded-full transition-all flex items-center w-fit text-sm py-1 px-1`}
          onClick={onTriggerDataScienceTask}
        ></i>
      </Tooltip>
    );
  };

  const renderEnableButton = () => {
    return (
      <Tooltip
        key={'enable-button-tooltip'}
        tooltipContent={<div className="whitespace-nowrap">Re-Enable</div>}
      >
        <i
          onClick={onEnableDataScienceTask}
          className="cursor-pointer ra-icons-generic-check mr-2 text-neutral-500 group-hover:text-success-400 transition-all flex items-center w-fit text-lg"
        ></i>
      </Tooltip>
    );
  };

  const renderDisableButton = () => {
    return (
      <Tooltip
        key={'disable-button-tooltip'}
        isDisabled={isProcessing}
        tooltipContent={<div className="whitespace-nowrap">Disable</div>}
      >
        <i
          className={`${
            isProcessing
              ? ''
              : 'group-hover:text-error-300 group-hover:border-error-300'
          } cursor-pointer ra-icons-generic-minus mr-2 text-neutral-500 border-2 rounded-full border-neutral-500  transition-all flex items-center rotate-45 w-fit text-xs`}
          onClick={onDisableDataScienceTask}
        ></i>
      </Tooltip>
    );
  };

  const renderEditButton = () => {
    return (
      <Tooltip
        key={'edit-button-tooltip'}
        tooltipContent={<div className="whitespace-nowrap">Edit</div>}
      >
        <i
          className={`cursor-pointer ra-icons-generic-edit mr-2 text-neutral-500 group-hover:text-success-400 transition-all flex items-center w-fit text-lg`}
          onClick={onEditDataScienceTask}
        ></i>
      </Tooltip>
    );
  };

  const renderReviewButton = () => {
    return (
      <Tooltip
        key={'review-button-tooltip'}
        tooltipContent={<div className="whitespace-nowrap">Review</div>}
      >
        <i
          className={`cursor-pointer  ra-icons-generic-page-1 mr-2 text-neutral-500 group-hover:text-success-400 transition-all flex items-center w-fit text-lg`}
          onClick={onReviewDataScienceTask}
        ></i>
      </Tooltip>
    );
  };

  const renderActionButtons = () => {
    switch (dataScienceTask.status) {
      case DataScienceTaskStatus.STAGING:
        return [
          renderReviewButton(),
          renderTriggerButton(),
          renderDisableButton(),
          renderEditButton()
        ];
      case DataScienceTaskStatus.DISABLED:
        return [renderEnableButton(), renderEditButton()];
      default:
        return [
          renderTriggerButton(),
          renderDisableButton(),
          renderEditButton()
        ];
    }
  };

  return (
    <div className="ml-2 text-sm w-full flex items-center justify-center">
      {renderActionButtons()}
    </div>
  );
};

export default DataScienceTaskTableActions;
